import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface MetricRow {
  readonly qualityMetricId: string;
  readonly metricCode: string;
  readonly scope: string;
  readonly metricValue: string;
  readonly targetValue: string | null;
  readonly isBreach: boolean;
}

const METRIC_CODES = ['', 'PASS_PCT', 'FAIL_PCT', 'BLOCKED_PCT', 'DEFECT_DENSITY', 'DEFECT_LEAKAGE', 'REQUIREMENT_COVERAGE', 'TEST_COVERAGE', 'EXECUTION_COVERAGE', 'REOPEN_RATE', 'CLOSURE_RATE'];
const SCOPES = ['', 'RELEASE', 'MODULE', 'PLAN', 'GLOBAL'];

/**
 * M34 — Quality Analytics Dashboard (§31 / §23). The ten tracked quality
 * indicators (T460) with measured value, target and breach flag, feeding M26
 * analytics. Filterable by metric code and scope.
 */
export function QualityAnalyticsDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<MetricRow[]>([]);
  const [metricCode, setMetricCode] = useState('');
  const [scope, setScope] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricCode) params.set('metricCode', metricCode);
    if (scope) params.set('scope', scope);
    const qs = params.toString();
    apiRequest<{ data: MetricRow[] }>(`/v1/qms/metrics${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricCode, scope]);

  return (
    <main aria-label="quality-analytics">
      <h1>{t('m34.qa.title')}</h1>
      <div className="filters">
        <label>
          {t('m34.qa.metric')}
          <select aria-label="filter-metric" value={metricCode} onChange={(e) => setMetricCode(e.target.value)}>
            {METRIC_CODES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.qa.scope')}
          <select aria-label="filter-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
            {SCOPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="metric-count">{t('m34.qa.count', { n: rows.length })}</p>
          <DataTable<MetricRow>
            ariaLabel="metric-table"
            rowKey={(r) => r.qualityMetricId}
            rows={rows}
            rowDataAttrs={(r) => ({ 'data-metric': r.metricCode, 'data-breach': String(r.isBreach) })}
            columns={[
              { key: 'metricCode', header: t('m34.qa.metric') },
              { key: 'scope', header: t('m34.qa.scope') },
              { key: 'metricValue', header: t('m34.qa.value') },
              { key: 'targetValue', header: t('m34.qa.target'), render: (r) => r.targetValue ?? '—' },
              { key: 'isBreach', header: t('m34.qa.breach'), render: (r) => (r.isBreach ? t('m34.common.yes') : t('m34.common.no')) },
            ]}
          />
        </>
      )}
    </main>
  );
}
